import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, Animated } from 'react-native';
import TopOpportunitiesCard from './TopOpportunitiesCard';
import { TopOpportunityItem } from './TopOpportunitiesTable';
import FontFamily from '../../../constants/fonts';

interface TopOpportunitiesContainerProps {
  title: string;
  count?: number;
  items: TopOpportunityItem[];
  width?: number | string;
  height?: number | string;
  onAddPress?: (item: TopOpportunityItem) => void;
  isLoading?: boolean;
}

  const formatToDDMM = (dateString?: string): string => {
    if (!dateString) return "";
    
    // Parse date string directly to avoid timezone conversion
    // Expected format: YYYY-MM-DD or ISO format (YYYY-MM-DDTHH:mm:ss)
    const datePart = dateString.split('T')[0]; // Handle ISO format with time
    const [year, month, day] = datePart.split('-');
    
    if (!year || !month || !day) return "";
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthIndex = parseInt(month, 10) - 1;
    const dayNum = parseInt(day, 10);
    
    return `${dayNum} ${monthNames[monthIndex]}`;
  };

// Skeleton component for loading state
const TopOpportunityCardSkeleton: React.FC = () => {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={styles.skeletonCard}>
      <View style={styles.skeletonHeader}>
        <Animated.View style={[styles.skeletonIcon, { opacity }]} />
        <View style={styles.skeletonTitleArea}>
          <Animated.View style={[styles.skeletonTitle, { opacity }]} />
          <Animated.View style={[styles.skeletonSubtitle, { opacity }]} />
        </View>
      </View>
      <Animated.View style={[styles.skeletonDescription, { opacity }]} />
      <View style={styles.skeletonFooter}>
        <Animated.View style={[styles.skeletonDate, { opacity }]} />
        <Animated.View style={[styles.skeletonButton, { opacity }]} />
      </View>
    </View>
  );
};


const TopOpportunitiesContainer: React.FC<TopOpportunitiesContainerProps> = ({
  title,
  count,
  items,
  width,
  height,
  onAddPress,
  isLoading = false,
}) => {
  const containerStyle: any = [styles.container];
  if (width !== undefined) containerStyle.push({ width });
  if (height !== undefined) containerStyle.push({ height });

  return (
    <View style={containerStyle}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{title}</Text>
        {count !== undefined && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{count}</Text>
          </View>
        )}
      </View>

      {/* Scrollable Body */}
      {isLoading ? (
        <ScrollView 
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          showsVerticalScrollIndicator={false}
        >
          {[...Array(3)].map((_, index) => (
            <View key={`skeleton-${index}`} style={styles.cardWrapper}>
              <TopOpportunityCardSkeleton />
            </View>
          ))}
        </ScrollView>
      ) : (
        <ScrollView 
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          showsVerticalScrollIndicator={true}
        >
          {items.map((item) => (
            <View key={item.ID} style={styles.cardWrapper}>
              <TopOpportunitiesCard
                title={item.TITLE}
                description={item?.ACTION_PLAN_DESCRIPTION || ''}
                taskType={item?.TYPE}
                date={formatToDDMM(item.TARGET_DATE)}
                onAddPress={() => onAddPress && onAddPress(item)}
                // onCardPress={item.onCardPress}
                status={item.STATUS}
                statusColor={item.statusColor}
              />
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding:16,
    backgroundColor: '#F5F9FF',
    borderRadius: 12,
    overflow: 'hidden',
    // borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  header: {
    backgroundColor: '#F7FAFC',
    paddingHorizontal: 16,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#4B4B4B',
    fontFamily: Platform.OS === 'ios' ? 'System' : FontFamily.medium,
  },
  badge: {
    backgroundColor: '#106E98',
    borderRadius: 10,
    minWidth: 24,
    height: 20,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontStyle: 'normal',
    fontFamily: FontFamily.medium,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  body: {
    flex: 1,
    backgroundColor: '#F7FAFC',
  },
  bodyContent: {
    padding: 12,
    gap: 10,
  },
  cardWrapper: {
    position: 'relative',
  },
  statusBadge: {
    position: 'absolute',
    top: 12,
    right: 40,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  loaderWrapper: {
    minHeight: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  skeletonCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  skeletonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  skeletonIcon: {
    width: 24,
    height: 24,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonTitleArea: {
    flex: 1,
    gap: 6,
  },
  skeletonTitle: {
    width: '70%',
    height: 18,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonSubtitle: {
    width: '40%',
    height: 14,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonDescription: {
    width: '90%',
    height: 14,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonDate: {
    width: 80,
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonButton: {
    width: 32,
    height: 32,
    backgroundColor: '#E0E0E0',
    borderRadius: 16,
  },
});

export default TopOpportunitiesContainer;
